import { createDefaultProviderReader } from './default-provider-carriers';
import type { DefaultProviderDependencies } from './default-provider-carriers';

/** ItemLayerTypeCatalog's loaded cache projection: catalog variable names and normalized SQL integers.
 * Preserve incoming row order; PHP's stable ties follow that order, not alphabetic name order.
 */
export type ProviderCatalogRows = Readonly<Record<string, {
    readonly default_provider?: number | null;
    readonly descent_priority?: number | null;
}>>;

/** The derivation is pure over the (immutable, loaded-once) catalog rows object: memoized per object (profiled 2026-09-22 — every
 * provider reader re-validated and re-sorted the whole layer-type catalog, many times per possessor compile). A copy is returned. */
const derivedProviderNames = new WeakMap<object, readonly string[]>();
export function deriveDefaultProviderNames(rows: ProviderCatalogRows): string[] {
    const hit = derivedProviderNames.get(rows);
    if (hit) return [...hit];
    const names = deriveDefaultProviderNamesUncached(rows);
    derivedProviderNames.set(rows, names);
    return [...names];
}
function deriveDefaultProviderNamesUncached(rows: ProviderCatalogRows): string[] {
    for (const [name, row] of Object.entries(rows)) {
        if (!/^[a-z_][a-z0-9_]*$/i.test(name)) throw new TypeError('Provider catalog requires variable-name keys');
        for (const key of ['default_provider', 'descent_priority'] as const) {
            const value = Object.prototype.hasOwnProperty.call(row, key) ? row[key] : null;
            if (value != null && (!Number.isInteger(value) || value < -2147483648 || value > 2147483647)) {
                throw new TypeError('Provider catalog requires normalized SQL integer metadata');
            }
        }
    }
    return Object.entries(rows)
        .filter(([, row]) => Object.prototype.hasOwnProperty.call(row, 'default_provider') && row.default_provider === 1)
        .map(([name, row], sequence) => ({ name, sequence,
            priority: Object.prototype.hasOwnProperty.call(row, 'descent_priority') ? row.descent_priority ?? 0 : 0 }))
        .sort((left, right) => right.priority - left.priority || left.sequence - right.sequence)
        .map(row => row.name);
}

/** Snapshot the catalog order into the same request-owned provider context used by the raw walker. */
export function createCatalogDefaultProviderReader(input: Omit<DefaultProviderDependencies, 'providerNames'> & {
    catalogRows: ProviderCatalogRows;
}): ReturnType<typeof createDefaultProviderReader> {
    return createDefaultProviderReader({ ...input, providerNames: deriveDefaultProviderNames(input.catalogRows) });
}
